/**
 * The header's two ways into AR: Quick Look on iOS, and the WebXR overlay
 * (which on iPhone has to hop through a Variant Launch App Clip first).
 *
 * Split out of Header.tsx: this is self-contained state with no other caller.
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { apiClient } from "../api/client";
import { getSceneUtils } from "../scene";
import { openAssemblyInAr, supportsArQuickLook } from "../scene/arQuickLook";
import {
  LIVE_AR_AUTO_OPEN_PARAM,
  LIVE_AR_TICKET_PARAM,
  loadVariantLaunch,
  variantLaunchUrl,
} from "../ar/variantLaunch";

export function useArEntry() {
  const [arBusy, setArBusy] = useState(false);
  const [arError, setArError] = useState<string | null>(null);
  const [liveArOpen, setLiveArOpen] = useState(false);
  // Stable identity: the overlay keys its start/stop effect on this callback,
  // so a fresh function every Header render would restart the camera whenever
  // unrelated header state changed.
  const handleCloseLiveAr = useCallback(() => setLiveArOpen(false), []);
  // Briefly tells the user what to press on the App Clip card, whose own
  // instructions are in English while the iOS button label is localized.
  const [arLaunchHint, setArLaunchHint] = useState(false);
  // On an iPhone with a Variant Launch key configured, WebXR only exists on
  // the far side of an App Clip hop: redirect there instead of opening the
  // overlay, and let the auto-open parameter bring Live AR up on return.
  // Everywhere else this resolves null and the overlay opens directly.
  const handleOpenLiveAr = useCallback(async () => {
    const vlaunch = await loadVariantLaunch();
    if (vlaunch?.launchRequired) {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set(LIVE_AR_AUTO_OPEN_PARAM, "1");
      // The App Clip's browser carries no Safari cookies, so without a
      // ticket the server rejects an owned session's websocket and the
      // robot vanishes into a fresh session on the other side.
      if (window.sessionId) {
        try {
          const ticket = await apiClient.createLiveArTicket(window.sessionId);
          if (ticket) {
            returnUrl.searchParams.set(LIVE_AR_TICKET_PARAM, ticket);
          }
        } catch (e) {
          // An unowned session crosses fine without one; an owned one will
          // land in a fresh session, which beats not launching at all.
          console.warn("Could not get a Live AR ticket:", e);
        }
      }
      // A beat to read the hint before the page navigates away.
      setArLaunchHint(true);
      const target = variantLaunchUrl(vlaunch, returnUrl.toString());
      window.setTimeout(() => {
        window.location.href = target;
      }, 1500);
      return;
    }
    setLiveArOpen(true);
  }, []);
  // Live AR needs a camera and a touch screen; a desktop with neither would
  // only ever reach the engine's unsupported-browser page.
  const canUseLiveAr = useMemo(
    () => typeof navigator !== "undefined" && navigator.maxTouchPoints > 0,
    []
  );
  // iOS only: everywhere else the link would download the file instead of
  // opening AR, so the entry is not offered at all.
  const canUseAr = useMemo(() => supportsArQuickLook(), []);

  // Back from the Variant Launch App Clip: the page has reloaded inside the
  // WebXR-capable Launch viewer, and the auto-open parameter says the user
  // was already on their way into Live AR — take them straight back there.
  // The same effect warms up the SDK so the button never waits on a script.
  useEffect(() => {
    if (!canUseLiveAr) {
      return;
    }
    void loadVariantLaunch();
    const params = new URLSearchParams(window.location.search);
    if (params.get(LIVE_AR_AUTO_OPEN_PARAM) === "1") {
      // Drop the parameters so exiting AR and reloading keeps the builder.
      // The ticket has already been read by the websocket URL, which is
      // computed at mount — before this effect runs.
      params.delete(LIVE_AR_AUTO_OPEN_PARAM);
      params.delete(LIVE_AR_TICKET_PARAM);
      const query = params.toString();
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}`
      );
      setLiveArOpen(true);
    }
  }, [canUseLiveAr]);

  const handleViewInAr = async (mount: "floor" | "wall" = "floor") => {
    const utils = getSceneUtils();
    if (!utils) {
      return;
    }
    setArBusy(true);
    setArError(null);
    try {
      await openAssemblyInAr(utils.getScene(), mount);
    } catch (e) {
      setArError(e instanceof Error ? e.message : String(e));
    } finally {
      setArBusy(false);
    }
  };

  return {
    canUseAr,
    canUseLiveAr,
    arBusy,
    arError,
    setArError,
    liveArOpen,
    arLaunchHint,
    handleOpenLiveAr,
    handleCloseLiveAr,
    handleViewInAr,
  };
}
